import type { ReactNode } from 'react';
import React, { forwardRef } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { noop } from 'lodash';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { themes } from './TabHeader.themes';
export type TabHeaderProps = {
  children: ReactNode;
  className?: string;
  color?: Colors;
  controlsId?: string;
  dataTestId?: string;
  iconName?: IconName;
  id?: string;
  isAccordion?: boolean;
  isActive?: boolean;
  isDisabled?: boolean;
  modifierClassNames?: {
    active?: string;
    inactive?: string;
  };
  onClick?: () => void;
  onKeyDown?: () => void;
  role?: string;
  style?: CSSRuleList;
  tabIndex?: number;
  theme?: keyof typeof themes;
  trackingData?: TrackingData | null | undefined;
};
export const TabHeader = forwardRef((props: TabHeaderProps, ref) => {
  const {
    children,
    className,
    controlsId,
    dataTestId = 'tab-header',
    iconName,
    id,
    isAccordion,
    isActive,
    isDisabled,
    onClick,
    onKeyDown,
    role,
    style,
    tabIndex,
    theme = 'default',
    trackingData,
  } = props;
  const tracker = useTracking();
  const track = !isActive && trackingData ? () => tracker(trackingData) : noop;
  const { thIcon, thIconWidth, thRoot } = themes[theme](props);
  const aria = {
    ...(controlsId
      ? {
          'aria-controls': controlsId,
        }
      : null),
    ...(isAccordion
      ? {
          'aria-expanded': isActive,
        }
      : {
          'aria-selected': isActive,
        }),
  };
  return (
    <button
      {...aria}
      className={classnames(thRoot, className)}
      data-cs-override-id="TabHeader" // ContentSquare must ignore this id see https://docs.contentsquare.com/uxa-fr/#ignorer-remplacer-ou-ajouter-un-id
      data-testid={dataTestId}
      disabled={isDisabled}
      onClick={() => {
        onClick && onClick();
        track();
      }}
      onKeyDown={onKeyDown}
      id={id}
      ref={ref}
      role={role}
      style={style}
      tabIndex={tabIndex}
      type="button"
    >
      {!!iconName && <Icon className={thIcon} name={iconName} width={thIconWidth} />}
      {children}
    </button>
  );
});
